import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  EmojiEvents as TrophyIcon,
  Psychology as AIIcon,
} from '@mui/icons-material';

interface CompetitorData {
  position: number;
  url: string;
  title: string;
  cqcScore: number;
  keywordDensity: number;
  entityDensity: number;
  wordCount: number;
  readabilityScore: number;
  questionsAnswered: number;
  aiOverviewPresence: boolean;
}

interface CompetitorComparisonProps {
  competitors: CompetitorData[];
  yourAnalysis: any;
  onMatchCompetitor: (index: number) => void;
}

export const CompetitorComparison: React.FC<CompetitorComparisonProps> = ({
  competitors,
  yourAnalysis,
  onMatchCompetitor
}) => {
  const getDifferenceColor = (yourValue: number, competitorValue: number) => {
    const diff = yourValue - competitorValue;
    if (Math.abs(diff) < 5) return 'default';
    return diff > 0 ? 'success' : 'error';
  };

  const renderMetricComparison = (yourValue: number, competitorValue: number, suffix: string = '') => {
    const diff = yourValue - competitorValue;
    const percentage = competitorValue ? ((yourValue / competitorValue - 1) * 100).toFixed(0) : 0;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="body2">
          {yourValue.toFixed(1)}{suffix}
        </Typography>
        {diff !== 0 && (
          <Chip
            size="small"
            label={`${diff > 0 ? '+' : ''}${percentage}%`}
            color={getDifferenceColor(yourValue, competitorValue)}
            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>
    );
  };

  const getPositionIcon = (position: number) => {
    if (position === 1) return <TrophyIcon sx={{ color: '#FFD700', fontSize: 20 }} />;
    if (position === 2) return <TrophyIcon sx={{ color: '#C0C0C0', fontSize: 18 }} />;
    if (position === 3) return <TrophyIcon sx={{ color: '#CD7F32', fontSize: 16 }} />;
    return position;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Competitor Analysis - Top 10 SERP Results
      </Typography>

      {!yourAnalysis && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Analyze your content first to see how you compare to competitors
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.light' }}>
              <TableCell>Rank</TableCell>
              <TableCell>Page</TableCell>
              <TableCell align="center">CQC Score</TableCell>
              <TableCell align="center">Keyword Density</TableCell>
              <TableCell align="center">Entity Density</TableCell>
              <TableCell align="center">Word Count</TableCell>
              <TableCell align="center">Readability</TableCell>
              <TableCell align="center">AI Overview</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Your Content Row */}
            {yourAnalysis && (
              <TableRow sx={{ bgcolor: 'info.light' }}>
                <TableCell>
                  <Chip label="YOU" color="primary" size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    Your Content
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={yourAnalysis.cqcScore}
                      size={40}
                      thickness={4}
                      color={yourAnalysis.cqcScore >= 80 ? 'success' : 'warning'}
                    />
                    <Box sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Typography variant="caption" fontWeight="bold">
                        {yourAnalysis.cqcScore}%
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="center">{yourAnalysis.keywordDensity.toFixed(1)}%</TableCell>
                <TableCell align="center">{yourAnalysis.entityDensity.toFixed(1)}</TableCell>
                <TableCell align="center">{yourAnalysis.wordCount}</TableCell>
                <TableCell align="center">{yourAnalysis.readabilityScore}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={`${yourAnalysis.aiOverviewProbability}%`}
                    size="small"
                    color={yourAnalysis.aiOverviewProbability >= 70 ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">-</TableCell>
              </TableRow>
            )}

            {/* Competitor Rows */}
            {competitors.map((competitor, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: competitor.position === 1 ? 'success.light' : 'transparent'
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getPositionIcon(competitor.position)}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={competitor.url}>
                    <Box>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {competitor.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" noWrap>
                        {new URL(competitor.url).hostname}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  {yourAnalysis ? (
                    renderMetricComparison(yourAnalysis.cqcScore, competitor.cqcScore, '%')
                  ) : (
                    <Typography variant="body2">{competitor.cqcScore}%</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {yourAnalysis ? (
                    renderMetricComparison(yourAnalysis.keywordDensity, competitor.keywordDensity, '%')
                  ) : (
                    <Typography variant="body2">{competitor.keywordDensity.toFixed(1)}%</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {yourAnalysis ? (
                    renderMetricComparison(yourAnalysis.entityDensity, competitor.entityDensity)
                  ) : (
                    <Typography variant="body2">{competitor.entityDensity.toFixed(1)}</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {yourAnalysis ? (
                    renderMetricComparison(yourAnalysis.wordCount, competitor.wordCount)
                  ) : (
                    <Typography variant="body2">{competitor.wordCount}</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {yourAnalysis ? (
                    renderMetricComparison(yourAnalysis.readabilityScore, competitor.readabilityScore)
                  ) : (
                    <Typography variant="body2">{competitor.readabilityScore}</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {competitor.aiOverviewPresence && (
                    <Chip
                      icon={<AIIcon />}
                      label="Yes"
                      size="small"
                      color="success"
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Match this competitor's metrics">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onMatchCompetitor(index)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View full content">
                      <IconButton
                        size="small"
                        onClick={() => window.open(competitor.url, '_blank')}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Insights */}
      {yourAnalysis && competitors.length > 0 && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Competitive Insights
          </Typography>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  Your Ranking Potential
                </Typography>
                <Typography variant="h4" color="primary">
                  #{Math.max(1, competitors.findIndex(c => 
                    yourAnalysis.cqcScore > c.cqcScore
                  ) + 1)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  Avg Competitor CQC
                </Typography>
                <Typography variant="h5">
                  {(competitors.reduce((sum, c) => sum + c.cqcScore, 0) / competitors.length).toFixed(0)}%
                </Typography>
                <Chip
                  size="small"
                  label={yourAnalysis.cqcScore > (competitors.reduce((sum, c) => sum + c.cqcScore, 0) / competitors.length) ? 'Above Avg' : 'Below Avg'}
                  color={yourAnalysis.cqcScore > (competitors.reduce((sum, c) => sum + c.cqcScore, 0) / competitors.length) ? 'success' : 'warning'}
                />
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  Top Performer Word Count
                </Typography>
                <Typography variant="h5">
                  {competitors[0]?.wordCount || 0}
                </Typography>
                <Typography variant="caption">
                  You: {yourAnalysis.wordCount} ({((yourAnalysis.wordCount / (competitors[0]?.wordCount || 1)) * 100).toFixed(0)}%)
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  AI Overview Leaders
                </Typography>
                <Typography variant="h5">
                  {competitors.filter(c => c.aiOverviewPresence).length}/{competitors.length}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(competitors.filter(c => c.aiOverviewPresence).length / competitors.length) * 100}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<TrophyIcon />}
              onClick={() => onMatchCompetitor(0)}
              sx={{
                background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
              }}
            >
              Match #1 Competitor Settings
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};